import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { useToast } from '../hooks/use-toast';
import { MapPin } from 'lucide-react';

const STATUS_OPTIONS = ['available', 'reserved', 'sold'];

const STATUS_COLORS = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  reserved: 'bg-amber-50 text-amber-700 border-amber-200',
  sold: 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_DOT = {
  available: 'bg-emerald-500',
  reserved: 'bg-amber-500',
  sold: 'bg-gray-400',
};

const AdminLots = () => {
  const [lots, setLots] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, reserved: 0, sold: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const { toast } = useToast();

  const loadLots = useCallback(async () => {
    try {
      const response = await adminService.getLots();
      setLots(response.data.lots || []);
      setStats(response.data.stats || {});
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load lots', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadLots(); }, [loadLots]);

  const handleStatusChange = async (lotId, newStatus) => {
    setUpdating(lotId);
    try {
      await adminService.updateLotStatus(lotId, newStatus);
      toast({ title: 'Updated', description: `Lot ${lotId} is now ${newStatus}` });
      loadLots();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading lots...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900" data-testid="admin-lots-title">Lot Inventory</h2>
        <p className="text-gray-600 mt-2">Manage industrial lot statuses</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total', value: stats.total, color: 'border-gray-300' },
          { label: 'Available', value: stats.available, color: 'border-emerald-400' },
          { label: 'Reserved', value: stats.reserved, color: 'border-amber-400' },
          { label: 'Sold', value: stats.sold, color: 'border-gray-400' },
        ].map((s) => (
          <div key={s.label} className={`bg-white border-l-4 ${s.color} rounded-lg shadow-sm p-5`} data-testid={`lot-stat-${s.label.toLowerCase()}`}>
            <p className="text-sm text-gray-500">{s.label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Lot Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-px bg-gray-200">
          {lots
            .sort((a, b) => parseInt(a.id) - parseInt(b.id))
            .map((lot) => (
            <div
              key={lot.id}
              data-testid={`admin-lot-${lot.id}`}
              className={`bg-white p-3 flex flex-col items-center gap-2 transition-all ${updating === lot.id ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm font-bold text-gray-900">Lot {lot.id}</span>
              </div>
              <span className="text-xs text-gray-500">
                {lot.m2 ? `${lot.m2.toLocaleString()} m2` : '-'}
              </span>
              <select
                value={lot.status}
                onChange={(e) => handleStatusChange(lot.id, e.target.value)}
                disabled={updating === lot.id}
                data-testid={`lot-status-select-${lot.id}`}
                className={`w-full text-xs font-semibold px-2 py-1.5 rounded-md border cursor-pointer ${STATUS_COLORS[lot.status]}`}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
              <div className={`w-2 h-2 rounded-full ${STATUS_DOT[lot.status]}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminLots;
